// @ts-strict-ignore
import * as React from 'react';
import { OrgComponentProps } from '../../typescriptHelpers';
import Space from 'antd/lib/space';
import Typography from 'antd/lib/typography';
import { useSelector } from 'react-redux';
import { selectAuthCredentials, selectOrg, selectUserCanPerformAction } from '../../selectors/selectors';
import { RootState } from '../../reducers/reducers';
import { antGrey, runwayColors, rwColors, tagStyle } from '../styles/globalStyles';
import { SSOConnectionState, SubscriptionFeatureType } from '../../types';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import * as API from '../../api';
import { globalMessage } from '../../utils/globalMessage';
import Text from 'antd/lib/typography/Text';
import Tag from 'antd/lib/tag';
import Modal from 'antd/lib/modal';
import Radio from 'antd/lib/radio';
import Tooltip from 'antd/lib/tooltip';
import { InfoCircleOutlined, SettingOutlined, WarningOutlined } from '@ant-design/icons';
import { DirectorySyncUserGroupsForGroups } from './DirectorySyncUserGroupsForGroups';
import { DirectorySyncGroupMappingSetting } from './DirectorySyncGroupMappingSetting';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import styles from '../styles/settingsStyles';

// Inherits the tooltip's own text color so the link stays legible on the dark tooltip surface in both themes.
const tooltipLinkStyle: React.CSSProperties = { color: 'inherit', textDecoration: 'underline' };

const directoryGroupsWarningTooltip = (
    <>
        This setting can be changed later. However, enabling Directory Sync without proper configuration of users and
        groups may result in unexpected overwrites to existing permissions in Runway, including pilot roles and
        rotations. Read our{' '}
        <a
            href="https://docs.runway.team/using-runway/organization-settings/sso-saml#directory-sync"
            target="_blank"
            rel="noreferrer"
            style={tooltipLinkStyle}
        >
            Directory Sync documentation
        </a>{' '}
        for more details.
    </>
);

// The icon is inline (not a Space item) so it trails the last word instead of detaching when the label wraps.
const labelWithTooltip = (label: React.ReactNode, tooltip: React.ReactNode, icon?: React.ReactNode) => (
    <>
        {label}
        &nbsp;
        <Tooltip title={tooltip}>{icon ?? <InfoCircleOutlined style={{ color: antGrey.grey6 }} />}</Tooltip>
    </>
);

export const SsoSamlSettings: React.FC<OrgComponentProps> = (props: OrgComponentProps) => {
    const org = useSelector((state: RootState) => selectOrg(state, props.orgId));
    const [generateSSOAdminPortalURLIsLoading, setGenerateSSOAdminPortalURLIsLoading] = React.useState(false);
    const [generateDirectorySyncAdminPortalURLIsLoading, setGenerateDirectorySyncAdminPortalURLIsLoading] =
        React.useState(false);
    const [isEnableDirectorySyncModalOpen, setIsEnableDirectorySyncModalOpen] = React.useState(false);
    const [shouldDirectoryManageGroups, setShouldDirectoryManageGroups] = React.useState(false);
    const [isSavingGroupMappingChoice, setIsSavingGroupMappingChoice] = React.useState(false);
    const authCredentials = useSelector((state: RootState) => selectAuthCredentials(state));
    const canUpdateGroupMapping = useSelector((state: RootState) =>
        selectUserCanPerformAction(state, RunwayUserAction.UAUpdateDirectorySyncUserGroupsForGroups),
    );
    const redirectToSSOAdminPortal = React.useCallback(() => {
        const redirectUrl = `${window.location.origin}${location.pathname}`;

        setGenerateSSOAdminPortalURLIsLoading(true);

        API.generateSSOAdminPortalRedirect(props.orgId, redirectUrl, authCredentials)
            .then((response) => {
                window.location.href = response.portalURL;
            })
            .catch((error) => {
                globalMessage.error(error.message);
            })
            .finally(() => {
                setGenerateSSOAdminPortalURLIsLoading(false);
            });
    }, [authCredentials]);
    const redirectToDirectorySyncAdminPortal = React.useCallback(() => {
        const redirectUrl = `${window.location.origin}${location.pathname}`;

        setGenerateDirectorySyncAdminPortalURLIsLoading(true);

        API.generateDirectorySyncAdminPortalRedirect(props.orgId, redirectUrl, authCredentials)
            .then((response) => {
                window.location.href = response.portalURL;
            })
            .catch((error) => {
                globalMessage.error(error.message);
            })
            .finally(() => {
                setGenerateDirectorySyncAdminPortalURLIsLoading(false);
            });
    }, [authCredentials]);
    const onDirectorySyncAdminPortalClick = React.useCallback(() => {
        if (org.ssoSamlConfig.ssoDirectory?.isActive) {
            redirectToDirectorySyncAdminPortal();
            return;
        }
        setShouldDirectoryManageGroups(false);
        setIsEnableDirectorySyncModalOpen(true);
    }, [org.ssoSamlConfig.ssoDirectory?.isActive, redirectToDirectorySyncAdminPortal]);
    const saveGroupMappingChoice = React.useCallback(async (): Promise<boolean> => {
        setIsSavingGroupMappingChoice(true);
        try {
            await API.setDisableDirectorySyncGroupMapping(
                { orgId: props.orgId, disabled: !shouldDirectoryManageGroups },
                authCredentials,
            );
            return true;
        } catch (error) {
            globalMessage.error(error.message);
            return false;
        } finally {
            setIsSavingGroupMappingChoice(false);
        }
    }, [shouldDirectoryManageGroups, authCredentials, props.orgId]);
    const onEnableDirectorySyncConfirmed = React.useCallback(async () => {
        if (!(await saveGroupMappingChoice())) {
            return;
        }
        redirectToDirectorySyncAdminPortal();
    }, [saveGroupMappingChoice, redirectToDirectorySyncAdminPortal]);
    // Connecting a directory means answering how user groups are managed, so until one is connected the portal
    // button is gated on that permission too.
    const directorySyncPortalActionType =
        org.ssoSamlConfig.ssoDirectory?.isActive || canUpdateGroupMapping ?
            RunwayUserAction.UAAccessDirectorySyncPortal
        :   RunwayUserAction.UAUpdateDirectorySyncUserGroupsForGroups;
    const colorForConnectionState = (status: SSOConnectionState) => {
        switch (status) {
            case SSOConnectionState.Active:
                return runwayColors.statusGreen;
            case SSOConnectionState.Inactive:
                return runwayColors.statusYellow;
            default:
                return runwayColors.statusYellow;
        }
    };
    const textForConnectionState = (status: SSOConnectionState) => {
        switch (status) {
            case SSOConnectionState.Active:
                return 'active';
            case SSOConnectionState.Inactive:
                return 'inactive';
            default:
                return 'not connected';
        }
    };
    const colorForDirectoryState = (isActive: boolean) => {
        return isActive ? runwayColors.statusGreen : runwayColors.statusYellow;
    };
    const textForDirectoryState = (isActive: boolean) => {
        return isActive ? 'active' : 'inactive';
    };

    return (
        <>
            <Modal
                title="About Directory Sync"
                open={isEnableDirectorySyncModalOpen}
                onOk={onEnableDirectorySyncConfirmed}
                onCancel={() => setIsEnableDirectorySyncModalOpen(false)}
                okText="Continue to Directory Sync admin portal"
                cancelText="Cancel"
                okButtonProps={{
                    loading: isSavingGroupMappingChoice || generateDirectorySyncAdminPortalURLIsLoading,
                }}
            >
                <Text style={{ display: 'block', marginBottom: 16 }}>
                    Once Directory Sync has been successfully connected, your users will be provisioned from your
                    directory and can no longer be added or removed from the Runway dashboard.
                </Text>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    {labelWithTooltip(
                        'Should Runway use your directory groups?',
                        directoryGroupsWarningTooltip,
                        <WarningOutlined style={{ color: rwColors.statusYellow }} />,
                    )}
                </Text>
                <Radio.Group
                    value={shouldDirectoryManageGroups}
                    onChange={(e) => setShouldDirectoryManageGroups(e.target.value)}
                >
                    <Space direction="vertical" size={12}>
                        <Radio value={true}>
                            {labelWithTooltip(
                                'Yes, update Runway groups based on your directory group changes',
                                'Groups from your directory provider define user groups in Runway (according to your configured mapping in Runway organization settings). User group assignments that already exist in Runway will be overwritten!',
                            )}
                        </Radio>
                        <Radio value={false}>No, manage Runway groups separately from directory groups</Radio>
                    </Space>
                </Radio.Group>
            </Modal>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    {org.ssoSamlConfig.ssoSamlEnabled ?
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                marginBottom: 32,
                                width: '50%',
                                minWidth: 500,
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    marginBottom: 32,
                                    width: '50%',
                                    minWidth: 500,
                                }}
                            >
                                <Space align="center" size={12}>
                                    <Text style={styles.appSettingsSectionTitleStyle}>
                                        SSO/SAML connection{' '}
                                        {org.ssoSamlConfig.ssoConnection?.name &&
                                            `(${org.ssoSamlConfig.ssoConnection.name})`}
                                    </Text>
                                    <Tag
                                        style={tagStyle}
                                        color={colorForConnectionState(org.ssoSamlConfig.ssoConnection?.state)}
                                    >
                                        {textForConnectionState(org.ssoSamlConfig.ssoConnection?.state)}
                                    </Tag>
                                </Space>
                                <Space direction="vertical" style={{ marginTop: 16 }}>
                                    <Text type="secondary">
                                        The SSO/SAML admin portal is hosted by our SSO provider, WorkOS. You must have
                                        the IT Admin permission in Runway to access the portal.
                                    </Text>
                                    <Space direction="vertical" style={{ marginTop: 24 }}>
                                        <PermissionedActionButton
                                            actionType={RunwayUserAction.UAAccessSSOPortal}
                                            icon={<SettingOutlined style={{ color: runwayColors.info }} />}
                                            loading={generateSSOAdminPortalURLIsLoading}
                                            onClick={redirectToSSOAdminPortal}
                                        >
                                            SSO/SAML admin portal
                                        </PermissionedActionButton>
                                    </Space>
                                </Space>
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    marginBottom: 32,
                                    width: '50%',
                                    minWidth: 500,
                                }}
                            >
                                <Space align="center" size={12}>
                                    <Text style={styles.appSettingsSectionTitleStyle}>
                                        Directory Sync{' '}
                                        {org.ssoSamlConfig.ssoDirectory?.name &&
                                            `(${org.ssoSamlConfig.ssoDirectory.name})`}
                                    </Text>
                                    <Tag
                                        style={tagStyle}
                                        color={colorForDirectoryState(org.ssoSamlConfig.ssoDirectory?.isActive)}
                                    >
                                        {textForDirectoryState(org.ssoSamlConfig.ssoDirectory?.isActive)}
                                    </Tag>
                                </Space>
                                <Space direction="vertical" style={{ marginTop: 16 }}>
                                    <Text type="secondary">
                                        The Directory Sync admin portal is hosted by our SSO/SAML provider, WorkOS. You
                                        must have the IT Admin permission in Runway to access the portal.
                                    </Text>
                                    <Space direction="vertical" style={{ marginTop: 24 }}>
                                        <PermissionedActionButton
                                            actionType={directorySyncPortalActionType}
                                            icon={<SettingOutlined style={{ color: runwayColors.info }} />}
                                            loading={generateDirectorySyncAdminPortalURLIsLoading}
                                            onClick={onDirectorySyncAdminPortalClick}
                                        >
                                            Directory Sync admin portal
                                        </PermissionedActionButton>
                                    </Space>
                                </Space>
                            </div>
                            {org.ssoSamlConfig.ssoDirectory?.isActive && (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        marginBottom: 32,
                                        minWidth: 500,
                                    }}
                                >
                                    <Space align="center" size={12}>
                                        <Text style={styles.appSettingsSectionTitleStyle}>
                                            Groups to Runway user groups
                                        </Text>
                                    </Space>
                                    <div>
                                        <Space direction="vertical" size={16} style={{ marginTop: 16 }}>
                                            <Text type="secondary">
                                                Runway can automatically assign Runway user groups to users based on the
                                                Directory Sync group or groups to which they belong to.
                                            </Text>
                                            <DirectorySyncGroupMappingSetting {...props} />
                                            <DirectorySyncUserGroupsForGroups {...props} />
                                        </Space>
                                    </div>
                                </div>
                            )}
                        </div>
                    :   <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.SAML} requiresTooltip={true}>
                            <Typography.Text>SSO/SAML is not enabled for your organization.</Typography.Text>
                        </PlanRestrictedFeatureWrapper>
                    }
                </div>
            </div>
        </>
    );
};
